import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiWrench } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'

/** Aggregate payload copied onto a suggestion issue by the correlation
 * engine (compliance/aggregates.go — spec 2026-07-17-01, lot 3). */
type SuggestionAggregate = {
    aggregate?: string
    count?: number
    window_months?: number
    value?: number
    intervention_uids?: string[]
}

type Props = {
    orgCode: string
    assetUid?: string
    aggregate: SuggestionAggregate
    /** Render the inner content without the outer Card chrome, so it can be
     * hosted inside another box (the ticket triptych's "Problème" card). */
    embedded?: boolean
}

/** One clickable proof line: the intervention's reference, title and date,
 * pointing at the asset's Interventions tab (interventions have no detail
 * page of their own — the tab is the canonical place to see them). */
function InterventionProofLine({ orgCode, assetUid, uid }: { orgCode: string; assetUid?: string; uid: string }) {
    const q = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/interventions/{interventionUid}',
        { params: { path: { orgCode, interventionUid: uid } } },
    )
    const iv = q.data
    const label = iv
        ? `${iv.reference ? iv.reference + ' — ' : ''}${iv.title ?? ''}${iv.performed_at ? ' (' + new Date(iv.performed_at).toLocaleDateString() + ')' : ''}`
        : uid.slice(0, 8) + '…'
    if (!assetUid) {
        return <li className="text-sm text-muted-foreground">{label}</li>
    }
    return (
        <li>
            <Link
                to="/$org/assets/$assetUid"
                params={{ org: orgCode, assetUid }}
                search={{ tab: 'interventions' }}
                className="text-sm text-primary hover:underline"
                data-testid={`suggestion-proof-${uid}`}
            >
                {label}
            </Link>
        </li>
    )
}

/** The proof card of a suggestion issue: states the measured gap (count over
 * window vs the declared norm) and lists the matched interventions, each
 * clickable. Without a clickable proof a suggestion is an opinion; with it,
 * it is a dossier (spec 2026-07-17-01). */
export function SuggestionProofCard({ orgCode, assetUid, aggregate, embedded = false }: Props) {
    const { t } = useTranslation('issues')
    const uids = aggregate.intervention_uids ?? []

    const body = (
        <div className="space-y-3">
            {aggregate.count != null && aggregate.window_months != null && (
                <p className="text-sm text-foreground" data-testid="suggestion-gap-line">
                    {t('suggestion.gap_line', {
                        count: aggregate.count,
                        months: aggregate.window_months,
                        norm: aggregate.value,
                    })}
                </p>
            )}
            {uids.length > 0 && (
                <ul className="space-y-1 ml-1">
                    {uids.map(uid => (
                        <InterventionProofLine key={uid} orgCode={orgCode} assetUid={assetUid} uid={uid} />
                    ))}
                </ul>
            )}
        </div>
    )

    if (embedded) {
        return (
            <div className="space-y-2" data-testid="suggestion-proof-card">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    <AppIcon path={mdiWrench} size="xs" />
                    {t('suggestion.card_title')}
                </div>
                {body}
            </div>
        )
    }

    return (
        <Card data-testid="suggestion-proof-card">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <AppIcon path={mdiWrench} size="sm" />
                    {t('suggestion.card_title')}
                </CardTitle>
            </CardHeader>
            <CardContent>{body}</CardContent>
        </Card>
    )
}
